import type { FC } from "react";
import type { Key } from "react-aria-components";

import { Dropdown } from "@/components/ui/dropdown/dropdown-index";

export type RowActionItem = {
	/** Shown under the label when the item is disabled (why it’s unavailable). */
	description?: string;
	icon: FC<{ className?: string }>;
	id: string;
	isDisabled?: boolean;
	label: string;
	onAction: () => void;
};

type RowActionsMenuProps = {
	ariaLabel: string;
	/** Disables the trigger (e.g. while another edit is pinned). */
	isDisabled?: boolean;
	items: RowActionItem[];
};

/**
 * Compact ⋮ menu used by location-management list and map sidebars.
 */
export const RowActionsMenu = ({
	ariaLabel,
	isDisabled = false,
	items,
}: RowActionsMenuProps) => {
	if (items.length === 0) return null;

	const handleAction = (key: Key) => {
		const item = items.find((entry) => entry.id === String(key));
		if (!item || item.isDisabled) return;
		item.onAction();
	};

	return (
		<div
			onClick={(event) => {
				event.stopPropagation();
			}}
			onKeyDown={(event) => {
				event.stopPropagation();
			}}
		>
			<Dropdown.Root>
				<Dropdown.DotsButton
					aria-label={ariaLabel}
					className="flex size-8 items-center justify-center rounded-lg text-fg-quaternary hover:bg-primary_hover disabled:cursor-not-allowed disabled:opacity-50"
					isDisabled={isDisabled}
				/>
				<Dropdown.Popover className="min-w-44" placement="bottom end">
					<Dropdown.Menu
						aria-label={ariaLabel}
						disabledKeys={
							new Set(
								items.filter((item) => item.isDisabled).map((item) => item.id),
							)
						}
						onAction={handleAction}
						selectionMode="none"
					>
						{items.map((item) => (
							<Dropdown.Item
								description={item.description}
								icon={item.icon}
								id={item.id}
								key={item.id}
								label={item.label}
							/>
						))}
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown.Root>
		</div>
	);
};
